/* ==============================================================
   AMIGO PROVISIONS — PDP interactivity
   --------------------------------------------------------------
   Custom element <amigo-product> wires up:
     - Color / size option buttons -> resolve to a variant, update
       the hidden form input, swap price + availability + selected
       option label, and (when the color has its own image) swap
       the primary image.
     - Thumbnail click -> swap the primary image, update mobile
       page dots.
     - Qty +/- buttons -> increment/decrement the qty input.
     - Sold-out / unavailable states disable Add to bag.

   No framework. Single class, single source of truth for the
   selected variant.
   ============================================================== */
(() => {
  class AmigoProduct extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-variants]');
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[amigo-product] failed to parse variants', e);
        return;
      }
      this.optionNames = (JSON.parse(this.dataset.optionNames || '[]'));
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.form = this.querySelector('form');
      this.variantIdInput = this.querySelector('input[name="id"]');
      this.priceValueEl = this.querySelector('[data-price-value]');
      this.priceCompareEl = this.querySelector('[data-price-compare]');
      this.stickyPriceEl = document.querySelector('[data-sticky-price]');
      this.addBtns = this.querySelectorAll('[data-add-btn]');
      this.bisBtn = this.querySelector('[data-bis-trigger]');
      this.gallery = this.querySelector('[data-gallery]');
      this.galleryImages = this.querySelectorAll('.pdp__primary-img');
      this.pageDots = this.querySelectorAll('.pdp__pagedot-btn');
      this.thumbBtns = this.querySelectorAll('.pdp__thumb');
      this.sizeChartDialog = this.querySelector('[data-size-chart-dialog]');

      this.selected = {};
      this.optionNames.forEach((name, i) => {
        const currentVariant = this.variants.find(v => v.id == this.variantIdInput.value);
        if (currentVariant) this.selected[name] = currentVariant.options[i];
      });

      this.addEventListener('click', this.handleClick.bind(this));
      this.setupGalleryObserver();
      this.setupSizeChartKeyboard();
    }

    // Esc closes the size guide. We drive the dialog via its `open`
    // attribute (not showModal), so we wire Esc ourselves instead of
    // relying on the native modal's built-in handling.
    setupSizeChartKeyboard() {
      if (!this.sizeChartDialog) return;
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sizeChartDialog.hasAttribute('open')) {
          this.closeSizeChart();
        }
      });
    }

    // Watch which slide is currently snapped into view and reflect
    // that in the dot + thumb active states. Uses IntersectionObserver
    // against the gallery root so it works on both mobile swipe AND
    // the desktop programmatic scroll.
    setupGalleryObserver() {
      if (!this.gallery || this.galleryImages.length < 2) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.intersectionRatio >= 0.5) {
            const idx = parseInt(entry.target.dataset.imageIndex, 10);
            this.markActiveImage(idx);
          }
        });
      }, { root: this.gallery, threshold: 0.5 });
      this.galleryImages.forEach(img => observer.observe(img));
    }

    markActiveImage(index) {
      this.pageDots.forEach((d, i) => {
        const on = i === index;
        d.classList.toggle('is-on', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.thumbBtns.forEach((t, i) => {
        t.classList.toggle('pdp__thumb--active', i === index);
      });
    }

    handleClick(e) {
      // Size guide modal: open button, close button, or dim-backdrop click.
      if (e.target.closest('[data-size-chart-open]')) {
        e.preventDefault();
        this.openSizeChart();
        return;
      }
      if (e.target.closest('[data-size-chart-close]')) {
        e.preventDefault();
        this.closeSizeChart();
        return;
      }
      if (this.sizeChartDialog && e.target === this.sizeChartDialog) {
        // Click landed on the <dialog> itself (its backdrop), not the card.
        this.closeSizeChart();
        return;
      }

      const optionBtn = e.target.closest('[data-option-name]');
      if (optionBtn) {
        e.preventDefault();
        this.selectOption(optionBtn);
        return;
      }
      const thumbBtn = e.target.closest('[data-thumb-index]');
      if (thumbBtn) {
        e.preventDefault();
        this.setActiveImage(parseInt(thumbBtn.dataset.thumbIndex, 10));
        return;
      }
      const qtyBtn = e.target.closest('[data-qty-step]');
      if (qtyBtn) {
        e.preventDefault();
        this.stepQty(parseInt(qtyBtn.dataset.qtyStep, 10));
        return;
      }
    }

    selectOption(btn) {
      const name = btn.dataset.optionName;
      const value = btn.dataset.optionValue;
      this.selected[name] = value;

      // Update the visible "selected" label for this option group
      const labelEl = this.querySelector(`[data-option-display="${name}"]`);
      if (labelEl) labelEl.textContent = value;

      // Update button active states within this group
      this.querySelectorAll(`[data-option-name="${name}"]`).forEach(el => {
        el.classList.toggle('pdp__colorbtn--active', el === btn && el.classList.contains('pdp__colorbtn'));
        el.classList.toggle('pdp__size--active', el === btn && el.classList.contains('pdp__size'));
      });

      // Resolve to a variant
      const variant = this.variants.find(v =>
        v.options.every((opt, i) => opt === this.selected[this.optionNames[i]])
      );

      if (!variant) {
        this.setAddState(false, 'Unavailable');
        return;
      }

      this.variantIdInput.value = variant.id;
      this.updatePrice(variant);
      this.setAddState(variant.available, variant.available ? null : 'Sold out');

      // Scroll the carousel to the variant's featured image if it
      // exists in the gallery. Compare against the data-image-index
      // values via src (the carousel renders product.images in order).
      if (variant.featured_image && variant.featured_image.src) {
        const matchIdx = Array.from(this.galleryImages).findIndex(img => {
          return img.src.split('?')[0] === variant.featured_image.src.split('?')[0]
            || (img.dataset.imageSrc && img.dataset.imageSrc === variant.featured_image.src);
        });
        if (matchIdx >= 0) this.setActiveImage(matchIdx);
      }

      // Reflect variant in the URL so refresh / share preserves selection
      if (history.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
    }

    // Programmatic scroll to image at index. Dot + thumb active
    // state updates happen automatically via the IntersectionObserver.
    setActiveImage(index) {
      const target = this.galleryImages[index];
      if (!target || !this.gallery) return;
      this.gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }

    openSizeChart() {
      const d = this.sizeChartDialog;
      if (!d) return;
      // Load the chart image on first open. It's deferred via data-src so
      // it isn't fetched on PDPs where the guide is never opened, and so
      // it loads reliably even though the dialog starts hidden (lazy
      // images inside a hidden <dialog> don't always load on iOS Safari).
      const img = d.querySelector('.pdp__sizeguide-img');
      if (img && !img.getAttribute('src') && img.dataset.src) {
        img.setAttribute('src', img.dataset.src);
      }
      // Toggle via the `open` attribute rather than showModal() so the
      // dialog renders as our own fixed overlay — predictable on iOS,
      // where the native modal was opening off-screen / invisible.
      d.setAttribute('open', '');
      document.body.classList.add('pdp-sizeguide-open');
      const closeBtn = d.querySelector('[data-size-chart-close]');
      if (closeBtn) closeBtn.focus();
    }

    closeSizeChart() {
      const d = this.sizeChartDialog;
      if (!d) return;
      d.removeAttribute('open');
      document.body.classList.remove('pdp-sizeguide-open');
    }

    stepQty(delta) {
      const qtyInput = this.querySelector('input[name="quantity"]');
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value, 10) || 1;
      const next = Math.max(1, current + delta);
      qtyInput.value = next;
    }

    updatePrice(variant) {
      const formatted = this.formatMoney(variant.price);
      if (this.priceValueEl) this.priceValueEl.textContent = formatted;
      if (this.stickyPriceEl) this.stickyPriceEl.textContent = formatted;
      if (this.priceCompareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.priceCompareEl.textContent = this.formatMoney(variant.compare_at_price);
          this.priceCompareEl.style.display = '';
        } else {
          this.priceCompareEl.style.display = 'none';
        }
      }
    }

    setAddState(available, label) {
      this.addBtns.forEach(btn => {
        btn.disabled = !available;
        const labelEl = btn.querySelector('[data-add-label]');
        if (labelEl && label) labelEl.textContent = label;
        if (labelEl && available && labelEl.dataset.original) {
          labelEl.textContent = labelEl.dataset.original;
        }
      });
      // Show the Klaviyo notify button only when the variant is sold out.
      if (this.bisBtn) this.bisBtn.hidden = available;
    }

    formatMoney(cents) {
      const dollars = (cents / 100).toFixed(2);
      // Replace the typical {{amount}} / {{amount_no_decimals}} tokens
      return this.moneyFormat
        .replace(/{{\s*amount\s*}}/, dollars)
        .replace(/{{\s*amount_no_decimals\s*}}/, Math.round(cents / 100).toString())
        .replace(/{{\s*amount_with_comma_separator\s*}}/, dollars.replace(/\./, ','));
    }
  }

  if (!customElements.get('amigo-product')) {
    customElements.define('amigo-product', AmigoProduct);
  }
})();
